/**
 * Blood Pressure Value Parsing Helpers
 *
 * These helpers parse blood pressure values from various formats:
 * - Object format: { systolic: 120, diastolic: 80 }
 * - String format: "120/80"
 * - JSON string format: '{"systolic":120,"diastolic":80}'
 * - Number format: 120 (systolic only)
 */

export interface BloodPressureData {
  systolic: number;
  diastolic: number;
}

export type BloodPressureValueInput =
  | string
  | number
  | object
  | null
  | undefined;

/**
 * Parse blood pressure value from object format
 * @param obj - Object containing systolic and diastolic values
 * @returns Parsed blood pressure data or null if invalid
 */
export const parseObjectValue = (
  obj: Record<string, unknown>,
): BloodPressureData | null => {
  if (typeof obj.systolic === "number" && typeof obj.diastolic === "number") {
    return {
      systolic: Number(obj.systolic) || 0,
      diastolic: Number(obj.diastolic) || 0,
    };
  }
  return null;
};

/**
 * Parse blood pressure value from string format
 * Handles: "120/80", JSON strings, and numeric extraction
 * @param value - String value to parse
 * @returns Parsed blood pressure data or null if invalid
 */
export const parseStringValue = (value: string): BloodPressureData | null => {
  // Handle "120/80" format
  if (value.includes("/")) {
    const parts = value.split("/");
    return {
      systolic: Number.parseFloat(parts[0]) || 0,
      diastolic: Number.parseFloat(parts[1]) || 0,
    };
  }

  // Handle JSON format or other formats
  try {
    const parsed = JSON.parse(value);
    if (parsed.systolic && parsed.diastolic) {
      return {
        systolic: Number.parseFloat(parsed.systolic) || 0,
        diastolic: Number.parseFloat(parsed.diastolic) || 0,
      };
    }
  } catch {
    // If parsing fails, try to extract numbers
    const numbers = value.match(/\d+/g);
    if (numbers && numbers.length >= 2) {
      return {
        systolic: Number.parseFloat(numbers[0]) || 0,
        diastolic: Number.parseFloat(numbers[1]) || 0,
      };
    }
  }

  return null;
};

/**
 * Main parser for blood pressure values from any supported format
 * @param value - Blood pressure value in any supported format
 * @returns Parsed blood pressure data with default values if parsing fails
 */
export const parseBloodPressureValue = (
  value: BloodPressureValueInput,
): BloodPressureData => {
  if (!value) {
    return { systolic: 0, diastolic: 0 };
  }

  // Handle object format (from valueJsonData.latest)
  if (typeof value === "object" && !Array.isArray(value)) {
    const result = parseObjectValue(value as Record<string, unknown>);
    if (result) return result;
  }

  // Handle number format
  if (typeof value === "number") {
    return { systolic: value, diastolic: 0 };
  }

  // Handle string format
  if (typeof value === "string") {
    const result = parseStringValue(value);
    if (result) return result;
  }

  return { systolic: 0, diastolic: 0 };
};
